import React, { useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Grid from "@material-ui/core/Grid";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LabelList,
} from "recharts";

import GridItem from "components/Grid/GridItem.js";
import GridContainer from "components/Grid/GridContainer";
import Greeting from "components/Greeting/Greeting";
//@ts-ignore
import { Map, CircleMarker, Tooltip as LeafletTooltip } from "react-leaflet";
import GoogleLayer from "components/Map/GoogleLayer.js";
import "components/Map/map-style.css";
import SpinLoading from "components/Loading/SpinLoading.js";
import Button from "@material-ui/core/Button";
import GetAppIcon from "@material-ui/icons/GetApp";
import { useRootSelector } from "hooks/reduxHooks";
import Reading from "spectral-core/models/Reading";
import { useUserLoggedIn } from "hooks/userHooks";
import { useGetReadings } from "hooks/readingsHooks";
import {
  aggregateDashboardOverview,
  applicationLabel,
} from "utils/dashboardAggregates";
import { exportMultiSheetExcel } from "utils/excelExport";

const CHART_BAR_COLORS = [
  "#00838f", "#26a69a", "#43a047", "#66bb6a",
  "#8bc34a", "#cddc39", "#ffb300", "#fb8c00",
  "#ef5350", "#ab47bc", "#5c6bc0", "#29b6f6",
];

//@ts-ignore
const useStyles = makeStyles({
  chartContainer: {
    padding: "16px",
    marginBottom: "16px",
  },
  chartTitle: {
    fontSize: "15px",
    fontWeight: 600,
    color: "#37474f",
    marginBottom: "8px",
  },
  kpiCard: {
    padding: "16px",
    textAlign: "center" as const,
    borderLeft: "4px solid #00838f",
    height: 110,
    display: "flex",
    flexDirection: "column" as const,
    justifyContent: "center",
    alignItems: "center",
  },
  kpiCardMain: {
    padding: "16px",
    textAlign: "center" as const,
    borderLeft: "4px solid #00838f",
    backgroundColor: "#e0f7fa",
    height: 110,
    display: "flex",
    flexDirection: "column" as const,
    justifyContent: "center",
    alignItems: "center",
  },
});

export default function Dashboard() {
  const classes = useStyles();

  const isLoggedIn = useUserLoggedIn();
  const { isFetching } = useGetReadings();

  const user = useRootSelector((state) => state.user.currentUser);
  const rawReadings = useRootSelector((state) => state.reading.readings) as Reading[] | undefined;

  const readings = useMemo(() => rawReadings ?? [], [rawReadings]);

  const overview = useMemo(
    () => (readings.length ? aggregateDashboardOverview(readings) : null),
    [readings]
  );

  const monthData = overview
    ? Object.keys(overview.byMonth).sort().map((k) => ({ name: k, value: overview.byMonth[k] }))
    : [];

  const applicationData = overview
    ? Object.entries(overview.byApplication)
        .map(([name, value]) => ({ name, value }))
        .sort((a, b) => b.value - a.value)
    : [];

  // Mapa com pins por aplicação
  const applicationColorMap = useMemo(() => {
    const map: Record<string, string> = {};
    let colorIdx = 0;
    for (const r of readings) {
      const app = applicationLabel(r);
      if (!map[app]) {
        map[app] = CHART_BAR_COLORS[colorIdx % CHART_BAR_COLORS.length];
        colorIdx++;
      }
    }
    return map;
  }, [readings]);

  const mapPins = useMemo(() => {
    const out: { position: [number, number]; label: string; application: string; color: string }[] = [];
    for (const r of readings) {
      try {
        if (!r.geoLocation || r.geoLocation.length < 10) continue;
        const { Latitude, Longitude } = JSON.parse(r.geoLocation);
        const app = applicationLabel(r);
        out.push({
          position: [Latitude, Longitude],
          label: r.name || "—",
          application: app,
          color: applicationColorMap[app] || "#9e9e9e",
        });
      } catch { /* skip */ }
    }
    return out;
  }, [readings, applicationColorMap]);

  // Legenda com contagem por aplicação (mesmo padrão da aba Coordenadas)
  const legendEntries = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const p of mapPins) counts[p.application] = (counts[p.application] || 0) + 1;
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .map(([name, count]) => ({
        name,
        count,
        color: applicationColorMap[name] || "#9e9e9e",
      }));
  }, [mapPins, applicationColorMap]);

  const dataLoading = !isLoggedIn || rawReadings === undefined;

  return (
    <div>
      {user && <Greeting user={user} />}

      {dataLoading && (
        <div style={{ textAlign: "center", padding: 40 }}>
          <SpinLoading />
          <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
            Carregando análises...
          </Typography>
        </div>
      )}

      {!dataLoading && (<>
      {/* Export Excel */}
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<GetAppIcon />}
          disabled={!overview}
          onClick={() => {
            if (!overview) return;
            const kpis = [
              { metrica: "Total de análises", valor: overview.totalReadings },
              { metrica: "Última sincronização", valor: overview.lastSync ? overview.lastSync.toLocaleString() : "—" },
              { metrica: "Temperatura média (°C)", valor: overview.avgTemperature != null ? overview.avgTemperature.toFixed(1) : "—" },
            ];
            const meses = monthData.map((m) => ({ mes: m.name, quantidade: m.value }));
            const aplicacoes = applicationData.map((a) => ({ aplicacao: a.name, quantidade: a.value }));
            const pinsRows = mapPins.map((p) => ({ nome: p.label, aplicacao: p.application, latitude: p.position[0], longitude: p.position[1] }));
            exportMultiSheetExcel(
              [
                { name: "KPIs", data: kpis, columns: [{ header: "Métrica", field: "metrica" }, { header: "Valor", field: "valor" }] },
                { name: "Por Mes", data: meses, columns: [{ header: "Mês", field: "mes" }, { header: "Quantidade", field: "quantidade" }] },
                { name: "Por Aplicacao", data: aplicacoes, columns: [{ header: "Aplicação", field: "aplicacao" }, { header: "Quantidade", field: "quantidade" }] },
                { name: "Mapa", data: pinsRows, columns: [{ header: "Nome", field: "nome" }, { header: "Aplicação", field: "aplicacao" }, { header: "Latitude", field: "latitude" }, { header: "Longitude", field: "longitude" }] },
              ],
              "VisaoGeral"
            );
          }}
        >
          Exportar Excel
        </Button>
      </div>

      {/* KPIs */}
      <Grid container spacing={2} style={{ marginBottom: 16 }}>
        <Grid item xs={12} sm={6} md={4} lg={4}>
          <Paper className={classes.kpiCardMain}>
            <Typography variant="caption">
              Total de análises{isFetching && " (carregando…)"}
            </Typography>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {isFetching && <SpinLoading />}
              <Typography variant="h4" style={{ fontWeight: 700, color: "#00838f" }}>
                {overview?.totalReadings ?? 0}
              </Typography>
            </div>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={4}>
          <Paper className={classes.kpiCard}>
            <Typography variant="caption">Última sincronização</Typography>
            <Typography variant="body1">
              {overview?.lastSync ? overview.lastSync.toLocaleString() : "—"}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={4}>
          <Paper className={classes.kpiCard}>
            <Typography variant="caption">Temperatura média (sensor)</Typography>
            <Typography variant="h6">
              {overview?.avgTemperature != null
                ? overview.avgTemperature.toFixed(1) + " °C"
                : "—"}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Gráficos: Volume por mês + por aplicação */}
      <GridContainer>
        {/*@ts-ignore*/}
        <GridItem xs={12} sm={12} md={6}>
          <Paper className={classes.chartContainer}>
            <Typography className={classes.chartTitle}>Análises por mês</Typography>
            {monthData.length ? (
              <ResponsiveContainer width="100%" height={Math.max(250, monthData.length * 38 + 40)}>
                <BarChart data={monthData} layout="vertical" margin={{ top: 5, right: 40, bottom: 5, left: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 12 }} />
                  <YAxis type="category" dataKey="name" width={80} tick={{ fontSize: 12 }} />
                  <Tooltip formatter={(val: any) => [val, "Quantidade"]} contentStyle={{ borderRadius: 8 }} />
                  <Bar dataKey="value" radius={[0, 4, 4, 0]} maxBarSize={28}>
                    {monthData.map((_e, idx) => (
                      <Cell key={idx} fill={CHART_BAR_COLORS[idx % CHART_BAR_COLORS.length]} />
                    ))}
                    <LabelList dataKey="value" position="right" style={{ fontSize: 12, fontWeight: 500 }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Typography variant="body2" color="textSecondary" style={{ textAlign: "center", padding: 40 }}>Sem dados</Typography>
            )}
          </Paper>
        </GridItem>
        {/*@ts-ignore*/}
        <GridItem xs={12} sm={12} md={6}>
          <Paper className={classes.chartContainer}>
            <Typography className={classes.chartTitle}>Análises por aplicação</Typography>
            {applicationData.length ? (
              <ResponsiveContainer width="100%" height={Math.max(250, applicationData.length * 38 + 40)}>
                <BarChart data={applicationData} layout="vertical" margin={{ top: 5, right: 40, bottom: 5, left: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 12 }} />
                  <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 12 }} />
                  <Tooltip formatter={(val: any) => [val, "Quantidade"]} contentStyle={{ borderRadius: 8 }} />
                  <Bar dataKey="value" radius={[0, 4, 4, 0]} maxBarSize={28}>
                    {applicationData.map((_e, idx) => (
                      <Cell key={idx} fill={CHART_BAR_COLORS[idx % CHART_BAR_COLORS.length]} />
                    ))}
                    <LabelList dataKey="value" position="right" style={{ fontSize: 12, fontWeight: 500 }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Typography variant="body2" color="textSecondary" style={{ textAlign: "center", padding: 40 }}>Sem dados</Typography>
            )}
          </Paper>
        </GridItem>
      </GridContainer>

      {/* Mapa */}
      <GridContainer>
        {/*@ts-ignore*/}
        <GridItem xs={12}>
          <Paper className={classes.chartContainer}>
            <Typography className={classes.chartTitle}>Mapa — análises por aplicação</Typography>
            {/* Mapa + Legenda lado a lado (mesmo padrão da aba Coordenadas) */}
            <div style={{ display: "flex", gap: 16, marginTop: 8 }}>
              <div style={{ flex: 1, height: "50vh", minWidth: 0 }}>
                {mapPins.length ? (
                  <Map
                    center={mapPins[0].position}
                    zoom={5}
                    style={{ height: "100%", borderRadius: 4 }}
                  >
                    <GoogleLayer />
                    {mapPins.map((pin, i) => (
                      <CircleMarker
                        key={i}
                        center={pin.position}
                        radius={6}
                        fillOpacity={0.7}
                        color={pin.color}
                        fillColor={pin.color}
                      >
                        <LeafletTooltip>
                          <span>{pin.label}</span><br />
                          <span>{pin.application}</span>
                        </LeafletTooltip>
                      </CircleMarker>
                    ))}
                  </Map>
                ) : (
                  <Typography variant="body2" color="textSecondary" style={{ textAlign: "center", padding: 40 }}>
                    Sem coordenadas nas leituras carregadas.
                  </Typography>
                )}
              </div>

              {/* Legenda à direita */}
              {legendEntries.length > 0 && (
                <div style={{
                  width: 220,
                  flexShrink: 0,
                  maxHeight: "50vh",
                  overflowY: "auto",
                  paddingRight: 4,
                }}>
                  <Typography variant="subtitle2" style={{ marginBottom: 8, fontWeight: 600, color: "#37474f" }}>
                    Aplicação
                  </Typography>
                  {legendEntries.map((entry) => (
                    <div
                      key={entry.name}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        marginBottom: 6,
                        fontSize: 13,
                        lineHeight: 1.3,
                      }}
                    >
                      <span style={{
                        width: 14,
                        height: 14,
                        borderRadius: "50%",
                        backgroundColor: entry.color,
                        display: "inline-block",
                        flexShrink: 0,
                      }} />
                      <span style={{ fontWeight: 500 }}>{entry.name}</span>
                      <span style={{ color: "#666", whiteSpace: "nowrap" }}>({entry.count})</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Paper>
        </GridItem>
      </GridContainer>
      </>)}
    </div>
  );
}
